
import React, { useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Button,
  Spin,
  Empty,
  Pagination,
  Badge,
  Progress,
} from "antd";
import {
  UserOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom"; 
import { useExpertServiceLearner } from "PlatForms/SelfLMS/Hooks/ExpertService/useExpertServiceLearner";
import SoftBox from "Common/hooks/StyleFeatures/SoftBox";
import SoftButton from "Common/hooks/StyleFeatures/SoftButton";
import SoftTypography from "Common/hooks/StyleFeatures/SoftTypography";

const { Title, Text } = Typography;

const PurchasedExpertServicesPage = () => {
  const navigate = useNavigate();
  const {
    purchasedServices,
    purchasedServicesLoading,
    purchasedServicesError,
    purchasedServicesPagination,
    fetchPurchasedServices
  } = useExpertServiceLearner();

  console.log("purchased expert services ........", purchasedServices);

  // Load purchased services on component mount
  useEffect(() => {
    fetchPurchasedServices();
  }, [fetchPurchasedServices]);

  // Handle page change
  const handlePageChange = (page) => {
    fetchPurchasedServices(page);
  };

  // Navigate to service details
  const viewServiceDetails = (serviceId) => {
    navigate(`/learner/expert-services/${serviceId}`);
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return "0 min";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  // Format price
  const formatPrice = (price, currency = "USD") => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate usage percentage
  const getUsagePercentage = (consumed, total) => {
    if (!total) return 0;
    return Math.min(100, Math.round((consumed / total) * 100));
  };

  // Render purchased service card
  const renderPurchasedServiceCard = (purchase) => {
    const { expertService, pricing, expert } = purchase;
    const usagePercentage = getUsagePercentage(purchase.consumedMinutes, purchase.totalMinutes);
    
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={purchase.id}>
        <Card
          hoverable
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column' } }}
          cover={
            <div style={{ 
              height: 120,
              background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5)), url(${expertService?.expert?.profileImage || '/default-expert.jpg'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'flex-end',
              padding: 12
            }}>
              <div style={{ color: 'white' }}>
                <Title level={5} style={{ color: 'white', margin: 0, fontSize: '16px' }}>
                  {expertService?.title}
                </Title>
              </div>
            </div>
          }
          actions={[
            <Button 
              key="view-details" 
              type="text" 
              onClick={() => viewServiceDetails(expertService?.id)}
            >
              View Details
            </Button>,
           
          ]}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Expert Info */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <Avatar 
                src={expertService?.expert?.profileImage} 
                icon={<UserOutlined />}
                size="small" 
                style={{ marginRight: 8 }}
              />
              <div>
                <Text strong>{expertService?.expert?.firstName} {expertService?.expert?.lastName}</Text>
                <div style={{ fontSize: '12px', color: '#57534E' }}>
                  {expertService?.expert?.expertCode}
                </div>
              </div>
            </div>
            
            {/* Purchase Info */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary">Purchased:</Text>
                <Text>{formatDate(purchase.paidAt)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary">Amount:</Text>
                <Text strong>{formatPrice(purchase.amount, purchase.currency)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary">Duration:</Text>
                <Text>{formatDuration(purchase.totalMinutes)}</Text>
              </div>
            </div>
            
            {/* Usage Progress */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary">Usage:</Text>
                <Text>{formatDuration(purchase.consumedMinutes)} / {formatDuration(purchase.totalMinutes)}</Text>
              </div>
              <Progress 
                percent={usagePercentage} 
                size="small" 
                status={usagePercentage >= 100 ? 'exception' : 'active'}
                strokeColor={usagePercentage >= 80 ? '#ff7875' : '#52c41a'}
              />
            </div>
            
            {/* Status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Badge 
                status={usagePercentage >= 100 ? 'default' : 'success'} 
                text={usagePercentage >= 100 ? 'Exhausted' : 'Active'} 
              />
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  if (purchasedServicesError) {
    return (
      <SoftBox py={3}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>Error Loading Purchased Services</Title>
          <Text>{purchasedServicesError}</Text>
          <Button type="primary" onClick={() => fetchPurchasedServices()} style={{ marginTop: 16 }}>
            Try Again
          </Button>
        </div>
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3}>
  {/* Header */}
<SoftBox mb={3} display="flex" alignItems="center">
  <SoftBox>
    <SoftTypography variant="h4" fontWeight="medium" style={{ color: "#1C1917" }}>
      My Purchased Services
    </SoftTypography>
    <SoftTypography variant="body2" style={{ color: "#57534E" }}>
      View and manage your purchased expert services
    </SoftTypography>
  </SoftBox>
</SoftBox>

      {/* Purchased Services Grid */}
      {purchasedServicesLoading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : purchasedServices.length === 0 ? (
        <Empty 
  description={
    <div>
      <p>You have not purchased any expert services yet</p>
      <p style={{ fontSize: '14px', color: '#57534E' }}>
        Expert services can be purchased when enrolling in courses that offer them
      </p>
    </div>
  } 
  style={{ padding: '50px' }}
/>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {purchasedServices.map(renderPurchasedServiceCard)}
          </Row>
          
          {/* Pagination */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Pagination
              current={purchasedServicesPagination.pageNumber}
              total={purchasedServicesPagination.totalCount}
              pageSize={purchasedServicesPagination.pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} of ${total} purchased services`
              }
            />
          </div>
        </>
      )}
    </SoftBox>
  );
};

export default PurchasedExpertServicesPage;